/**
 * Task #704 — Shared building blocks for the Approvals workflow UI.
 *
 * Exports:
 *   - <ApprovalPoliciesPanel/>     Admin Panel section: set workspace-wide
 *                                  thresholds per category. Default OFF.
 *   - <ApprovalOverridesPanel/>    Manager Panel section: stricter
 *                                  overrides for the manager's subtree.
 *   - <ApprovalsInbox/>            List of pending requests assigned to
 *                                  the caller (or admin escalations).
 */
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, ShieldCheck, CheckCircle2, XCircle, ClipboardCheck, AlertTriangle, CornerUpRight } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type {
  ApprovalCategory,
  ApprovalPolicy,
  ApprovalOverride,
  ApprovalRequest,
  ManagerSeatRequest,
} from "@shared/schema";

const CATEGORY_META: Record<ApprovalCategory, { label: string; unit: string; description: string; numeric: boolean }> = {
  discount: {
    label: "Discounts",
    unit: "%",
    description: "Require approval for invoice discounts above the chosen percentage.",
    numeric: true,
  },
  refund: {
    label: "Refunds",
    unit: "$",
    description: "Require approval for refund amounts above the chosen dollar value.",
    numeric: true,
  },
  contract_exception: {
    label: "Contract exceptions",
    unit: "",
    description: "Require approval when reps edit any of the flagged contract fields.",
    numeric: false,
  },
};

const CONTRACT_FIELD_OPTIONS = [
  { value: "cancellation_terms", label: "Cancellation terms" },
  { value: "late_fee_schedule", label: "Late fee schedule" },
  { value: "payment_terms", label: "Payment terms" },
  { value: "deposit_requirements", label: "Deposit requirements" },
  { value: "rider_attachments", label: "Rider attachments" },
];

// ── Admin: workspace policies ─────────────────────────────────────────────
export function ApprovalPoliciesPanel() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<{ policies: ApprovalPolicy[] }>({
    queryKey: ["/api/approvals/policies"],
  });

  if (isLoading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-start gap-3 p-3 rounded-md border bg-muted/30">
        <ShieldCheck className="w-5 h-5 text-primary mt-0.5" />
        <div className="text-sm">
          <p className="font-medium">Workspace approval policies</p>
          <p className="text-muted-foreground">
            All categories default OFF. When enabled, any action above the threshold is held until a manager or admin approves it.
            Managers can tighten — never loosen — these settings for their own team in the Manager Panel.
          </p>
        </div>
      </div>

      {data?.policies.map((p) => (
        <PolicyRow key={p.category} policy={p} />
      ))}
    </div>
  );
}

function PolicyRow({ policy }: { policy: ApprovalPolicy }) {
  const meta = CATEGORY_META[policy.category as ApprovalCategory];
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(!!policy.enabled);
  const [threshold, setThreshold] = useState<string>(policy.thresholdValue?.toString() ?? "");
  const [fields, setFields] = useState<string[]>(policy.contractFields || []);

  const mut = useMutation({
    mutationFn: async () => {
      return apiRequest("PUT", `/api/approvals/policies/${policy.category}`, {
        enabled,
        thresholdValue: meta.numeric && threshold !== "" ? Number(threshold) : null,
        contractFields: meta.numeric ? [] : fields,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/approvals/policies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/approvals/overrides"] });
      toast({ title: "Policy saved", description: `${meta.label} updated.` });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{meta.label}</CardTitle>
            <CardDescription>{meta.description}</CardDescription>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid={`switch-policy-${policy.category}`}
          />
        </div>
      </CardHeader>
      {enabled && (
        <CardContent className="space-y-3">
          {meta.numeric ? (
            <div className="space-y-1">
              <Label htmlFor={`threshold-${policy.category}`}>
                Approval required above ({meta.unit})
              </Label>
              <Input
                id={`threshold-${policy.category}`}
                type="number"
                min={0}
                step="0.01"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                placeholder="0"
                data-testid={`input-policy-threshold-${policy.category}`}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Fields that trigger approval</Label>
              <div className="grid grid-cols-2 gap-2">
                {CONTRACT_FIELD_OPTIONS.map((f) => (
                  <label key={f.value} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={fields.includes(f.value)}
                      onCheckedChange={(c) => setFields(
                        c ? [...fields, f.value] : fields.filter((x) => x !== f.value),
                      )}
                      data-testid={`checkbox-policy-field-${f.value}`}
                    />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <Button
              onClick={() => mut.mutate()}
              disabled={mut.isPending}
              data-testid={`button-save-policy-${policy.category}`}
            >
              {mut.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ── Admin: pending seat requests from managers ───────────────────────────
export function AdminSeatRequestsPanel() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<{ requests: ManagerSeatRequest[] }>({
    queryKey: ["/api/admin/seat-requests"],
  });
  const decide = useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: "approve" | "reject" }) =>
      apiRequest("POST", `/api/admin/seat-requests/${id}/decide`, { decision }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/seat-requests"] });
      toast({ title: "Seat request updated" });
    },
    onError: (e: any) => toast({ title: "Failed", description: e.message, variant: "destructive" }),
  });

  const rows = data?.requests || [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pending seat requests</CardTitle>
        <CardDescription>
          Managers asking for a new teammate. Approve to send the invite; reject to deny.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="p-3 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending seat requests.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {rows.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between gap-3 px-3 py-2"
                data-testid={`admin-seat-request-${r.id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{r.email}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {r.role}{r.reason ? ` · ${r.reason}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => decide.mutate({ id: r.id, decision: "reject" })}
                    disabled={decide.isPending}
                    data-testid={`button-reject-seat-${r.id}`}
                  >
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => decide.mutate({ id: r.id, decision: "approve" })}
                    disabled={decide.isPending}
                    data-testid={`button-approve-seat-${r.id}`}
                  >
                    Approve
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ── Manager: overrides ───────────────────────────────────────────────────
export function ApprovalOverridesPanel() {
  const { data, isLoading } = useQuery<{
    overrides: { category: ApprovalCategory; override: ApprovalOverride | null; policy: ApprovalPolicy | null }[];
  }>({ queryKey: ["/api/approvals/overrides"] });

  if (isLoading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-start gap-3 p-3 rounded-md border bg-muted/30">
        <ShieldCheck className="w-5 h-5 text-primary mt-0.5" />
        <div className="text-sm">
          <p className="font-medium">Stricter thresholds for your team</p>
          <p className="text-muted-foreground">
            Tighten the workspace defaults for your reports only. You can never set a looser threshold than the workspace policy — the form blocks it.
          </p>
        </div>
      </div>

      {data?.overrides.map((row) => (
        <OverrideRow key={row.category} {...row} />
      ))}
    </div>
  );
}

function OverrideRow({
  category, override, policy,
}: {
  category: ApprovalCategory;
  override: ApprovalOverride | null;
  policy: ApprovalPolicy | null;
}) {
  const meta = CATEGORY_META[category];
  const { toast } = useToast();
  const [threshold, setThreshold] = useState<string>(override?.thresholdValue?.toString() ?? "");
  const [fields, setFields] = useState<string[]>(override?.contractFields || policy?.contractFields || []);

  const adminEnabled = !!policy?.enabled;

  const save = useMutation({
    mutationFn: async () => apiRequest("PUT", `/api/approvals/overrides/${category}`, {
      thresholdValue: meta.numeric && threshold !== "" ? Number(threshold) : null,
      contractFields: meta.numeric ? [] : fields,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/approvals/overrides"] });
      toast({ title: "Override saved", description: `${meta.label} updated for your team.` });
    },
    onError: (e: any) => toast({ title: "Couldn't save", description: e.message, variant: "destructive" }),
  });

  const clear = useMutation({
    mutationFn: async () => apiRequest("DELETE", `/api/approvals/overrides/${category}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/approvals/overrides"] });
      setThreshold("");
      setFields(policy?.contractFields || []);
      toast({ title: "Override cleared", description: `${meta.label} now uses the workspace default.` });
    },
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{meta.label}</CardTitle>
            <CardDescription>{meta.description}</CardDescription>
          </div>
          <Badge variant={adminEnabled ? "secondary" : "outline"}>
            {adminEnabled ? "Workspace: ON" : "Workspace: OFF"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!adminEnabled ? (
          <div className="flex items-start gap-2 text-sm text-muted-foreground p-3 border rounded-md">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <span>Admin hasn't turned on this category yet — overrides are disabled until they do.</span>
          </div>
        ) : meta.numeric ? (
          <div className="space-y-1">
            <Label htmlFor={`override-${category}`}>
              Your team's threshold ({meta.unit}) — must be lower than {policy?.thresholdValue ?? "workspace value"}
            </Label>
            <Input
              id={`override-${category}`}
              type="number"
              min={0}
              step="0.01"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder={`Workspace: ${policy?.thresholdValue ?? "n/a"}`}
              data-testid={`input-override-threshold-${category}`}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <Label>Add fields beyond the workspace defaults</Label>
            <div className="grid grid-cols-2 gap-2">
              {CONTRACT_FIELD_OPTIONS.map((f) => {
                const locked = policy?.contractFields?.includes(f.value);
                return (
                  <label key={f.value} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      disabled={locked}
                      checked={fields.includes(f.value)}
                      onCheckedChange={(c) => setFields(
                        c ? [...fields, f.value] : fields.filter((x) => x !== f.value),
                      )}
                      data-testid={`checkbox-override-field-${f.value}`}
                    />
                    <span className={locked ? "text-muted-foreground" : ""}>
                      {f.label}{locked ? " (workspace)" : ""}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
        {adminEnabled && (
          <div className="flex justify-end gap-2">
            {override && (
              <Button variant="outline" onClick={() => clear.mutate()} disabled={clear.isPending} data-testid={`button-clear-override-${category}`}>
                Use workspace default
              </Button>
            )}
            <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid={`button-save-override-${category}`}>
              {save.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Approvals inbox ─────────────────────────────────────────────────────
export function ApprovalsInbox({ scope }: { scope: "manager" | "admin" }) {
  const { data, isLoading } = useQuery<{ requests: ApprovalRequest[] }>({
    queryKey: ["/api/approvals/requests", "inbox"],
    queryFn: async () => {
      const r = await fetch(`/api/approvals/requests?box=inbox`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load");
      return r.json();
    },
  });

  const { data: mine } = useQuery<{ requests: ApprovalRequest[] }>({
    queryKey: ["/api/approvals/requests", "mine"],
    queryFn: async () => {
      const r = await fetch(`/api/approvals/requests?box=mine`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load");
      return r.json();
    },
  });

  if (isLoading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }

  const pending = (data?.requests || []).filter((r) => r.status === "pending");
  const decided = (data?.requests || []).filter((r) => r.status !== "pending");

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4" /> Pending — assigned to you
          <Badge variant="secondary">{pending.length}</Badge>
        </h3>
        {pending.length === 0 ? (
          <div className="text-sm text-muted-foreground border rounded-md p-4">
            Nothing waiting for your decision.
          </div>
        ) : (
          <div className="space-y-2">
            {pending.map((r) => <RequestCard key={r.id} request={r} canDecide />)}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2">Recently decided</h3>
        {decided.length === 0 ? (
          <div className="text-sm text-muted-foreground border rounded-md p-4">No decisions yet.</div>
        ) : (
          <div className="space-y-2">
            {decided.slice(0, 20).map((r) => <RequestCard key={r.id} request={r} canDecide={false} />)}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2">Your submissions</h3>
        {(mine?.requests || []).length === 0 ? (
          <div className="text-sm text-muted-foreground border rounded-md p-4">No requests submitted yet.</div>
        ) : (
          <div className="space-y-2">
            {(mine?.requests || []).slice(0, 20).map((r) => <RequestCard key={r.id} request={r} canDecide={false} />)}
          </div>
        )}
      </div>
    </div>
  );
}

type DecideAction = "approved" | "rejected" | "forward_to_admin";

const ACTION_TITLES: Record<DecideAction, string> = {
  approved: "Approve request",
  rejected: "Reject request",
  forward_to_admin: "Forward to admin",
};

const ACTION_TOASTS: Record<DecideAction, string> = {
  approved: "approved",
  rejected: "rejected",
  forward_to_admin: "forwarded to a workspace admin",
};

function RequestCard({ request, canDecide }: { request: ApprovalRequest; canDecide: boolean }) {
  const { toast } = useToast();
  const [decideOpen, setDecideOpen] = useState<null | DecideAction>(null);
  const [note, setNote] = useState("");

  const decide = useMutation({
    mutationFn: async (decision: DecideAction) => {
      return apiRequest("POST", `/api/approvals/requests/${request.id}/decide`, { decision, note });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/approvals/requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/approvals/inbox-count"] });
      toast({
        title: "Request updated",
        description: `Request ${decideOpen ? ACTION_TOASTS[decideOpen] : "saved"}.`,
      });
      setDecideOpen(null);
      setNote("");
    },
    onError: (e: any) => toast({ title: "Couldn't save decision", description: e.message, variant: "destructive" }),
  });

  const statusBadge = request.status === "pending"
    ? <Badge variant="secondary">Pending</Badge>
    : request.status === "approved"
    ? <Badge className="bg-green-600 hover:bg-green-700">Approved</Badge>
    : <Badge variant="destructive">{request.status}</Badge>;

  const meta = CATEGORY_META[request.category as ApprovalCategory];
  const p = (request.payload || {}) as {
    percent?: number;
    amountDollars?: number;
    changedFields?: string[];
  };
  const valueDisplay = request.category === "discount"
    ? `${p.percent ?? "?"}%`
    : request.category === "refund"
    ? `$${p.amountDollars ?? "?"}`
    : `${(p.changedFields || []).join(", ")}`;

  return (
    <Card data-testid={`card-approval-request-${request.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{meta?.label || request.category}</span>
              <Badge variant="outline">{valueDisplay}</Badge>
              {statusBadge}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {request.summary || "(no summary)"}
            </p>
            {request.effectiveThreshold != null && (
              <p className="text-xs text-muted-foreground mt-1">
                Triggered above {request.effectiveThreshold}{meta?.unit ?? ""} ({request.thresholdSource})
              </p>
            )}
            {request.decisionNote && (
              <p className="text-xs italic text-muted-foreground mt-1">"{request.decisionNote}"</p>
            )}
          </div>
          {canDecide && request.status === "pending" && (
            <div className="flex gap-2 shrink-0 flex-wrap justify-end">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDecideOpen("forward_to_admin")}
                data-testid={`button-forward-${request.id}`}
                title="Decline to decide — reassign to a workspace admin."
              >
                <CornerUpRight className="w-4 h-4 mr-1" /> Forward
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDecideOpen("rejected")}
                data-testid={`button-reject-${request.id}`}
              >
                <XCircle className="w-4 h-4 mr-1" /> Reject
              </Button>
              <Button
                size="sm"
                onClick={() => setDecideOpen("approved")}
                data-testid={`button-approve-${request.id}`}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
              </Button>
            </div>
          )}
        </div>
      </CardContent>

      <Dialog open={!!decideOpen} onOpenChange={(o) => !o && setDecideOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{decideOpen ? ACTION_TITLES[decideOpen] : ""}</DialogTitle>
            <DialogDescription>
              {decideOpen === "forward_to_admin"
                ? "Reassign this request to a workspace admin. They'll receive an in-app and email notification."
                : "Optionally add a note. The requester will receive an in-app and email notification."}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            rows={4}
            data-testid="textarea-decision-note"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideOpen(null)}>Cancel</Button>
            <Button
              onClick={() => decideOpen && decide.mutate(decideOpen)}
              disabled={decide.isPending}
              data-testid="button-confirm-decision"
            >
              {decide.isPending
                ? "Saving..."
                : decideOpen === "approved"
                  ? "Approve"
                  : decideOpen === "rejected"
                    ? "Reject"
                    : "Forward"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
